/**
 * User-facing labels for installment checkout: Slice Pay vs Defera Capital (platform setting).
 * Use {@link getInstallmentsCopy} or {@link useInstallmentsCopy} — never hard-code "Slice Pay" in UI.
 */

const SLICE = {
  isDeferaCapital: false,
  /** Short name */
  brand: 'Slice Pay',
  brandLLC: 'Slice Pay LLC',
  brandPossessive: "Slice Pay's",
  partnerLong: 'Slice Pay',
  /** Parenthetical after "financial partner" */
  financialPartnerParen: 'Slice Pay',
  /** "to X" / "through X" for installment flow */
  installmentsToPhrase: "to Defera's financial partner (Slice Pay)",
  /** Confirmation of payment completion */
  confirmationOfPaymentPhrase: "Upon Slice Pay's confirmation of full payment completion",
  /** "Defera utilizes X to facilitate payment collection" */
  facilitatesCollection: 'Defera utilizes Slice Pay to facilitate payment collection on its behalf.',
  /** Charged by */
  chargedBy: 'Slice Pay',
  /** "paying X the unpaid balance" */
  payUnpaidTo: 'Slice Pay',
  /** coordinate with */
  coordinateWith: 'Slice Pay',
  /** governed by X's policies */
  governedByPolicies: "Slice Pay's policies and terms of service",
  /** financial partner (X) in identity section */
  financialPartnerIdentity: 'Slice Pay',
  /** shared with */
  sharedWith: 'Slice Pay',
  /** "Defera and X" payment reminders */
  deferaAndPartner: 'Defera and Slice Pay',
  /** "If we or X need to contact" */
  weOrPartner: 'If we or Slice Pay need to contact you',
  /** consent enumeration */
  consentParties: 'Defera, Slice Pay,',
  /** "amounts owed to X" - line 203 */
  owedToCreditor: 'amounts owed to Defera (as creditor of record)',
  /** paid to partner */
  paidToPartner: 'paid to Slice Pay',
  /** adjusted per */
  adjustedPer: "Slice Pay's policies",
  /** Section 2.8 heading */
  partnershipSectionTitle: 'Partnership with Slice Pay',
  /** Full H3 including "2.8." for Terms page */
  termsSection28Heading: '2.8. Creditor of Record and Partnership with Slice Pay',
  /** Travel product owner sentence */
  travelProductOwner: 'Slice Pay LLC owns the travel product (itinerary and booking arrangements) after booking, and all payments are made to Slice Pay.',
  /** Loss of eligibility */
  lossEligibility: 'Defera LLC and Slice Pay LLC services',
  /** verification "by Defera or X" */
  verificationBy: 'Defera or Slice Pay',
  /** confirmed by both */
  confirmedByBoth: 'both Defera and Slice Pay',
  /** payment methods approved by */
  approvedByForRecurring: 'Slice Pay',
  /** subject to X verification */
  subjectToVerification: "Slice Pay's verification and approval processes",
  /** payment dispute partner */
  paymentDisputePartner: 'Slice Pay (our payment processing partner)',
  /** Privacy section title */
  privacyPartnershipTitle: 'Partnership with Slice Pay',
  /** Privacy: partners with */
  privacyPartnersWith: 'Defera partners with Slice Pay, a licensed financial services provider',
  /** responsibilities heading */
  partnerResponsibilitiesTitle: "Slice Pay's responsibilities:",
  /** third party privacy */
  thirdPartyPrivacy: "Slice Pay's privacy policy",
  /** Analytics */
  analyticsPartnerName: 'Slice Pay',
  analyticsFeesLabel: 'Slice Pay Fees',
  analyticsViewLabel: 'Slice Pay View',
  /** Payment portal redirect */
  redirectDialogTitle: 'Redirecting to Slice Pay',
  redirectDialogBody:
    "You are being redirected to Slice Pay's portal. Use the email used on your checkout link for access.",
  /** Extension modal aside */
  extensionAsideDefera: 'separate from your Slice Pay installment obligations',
  /** Review checkbox — industry names */
  defaultIndustryEntities: 'Defera LLC, Slice Pay LLC, and other travel installment companies in the industry',
  /** FlightDetailsModal default message */
  defaultStandingJeopardy: 'Defera and Slice Pay',
  /** FlightDetailsModal cancel paragraph */
  defaultCancelInstallment: 'Defera and Slice Pay',
  /** EditCart link label */
  externalPaymentLinkLabel: 'Slice Pay Link',
  /** Payments page */
  webhookDescription: 'Slice Pay payment webhook logs',
  /** Admin modal */
  adminBookingRefLabel: 'Booking Reference (Slice Pay)',
  adminBookingRefHint: 'Payment reference used with Slice Pay (always visible to admins)',
  /** Carts / Accounting small labels */
  awaitingPartner: 'Awaiting SlicePay',
  /** Payment reminder senders (2.1.3) */
  remindersSentBy: 'Slice Pay or Defera',
  /** E-ticket email timing (2.1.4) */
  afterPartnerConfirmsEticket: 'after Slice Pay confirms',
  /** HowItWorks / terms variant for "pay Total Payments to X" */
  totalPaymentsCommitmentTarget: "Slice Pay (Defera's financial partner)",
  /** Warranty disclaimer (2.8) */
  noWarrantyFinancialServices:
    'Defera makes no representations or warranties regarding the financial services provided by Slice Pay and disclaims all liability for any issues arising from payment processing operations conducted by Slice Pay on Defera\'s behalf.',
  /** 2.5 communications list */
  communicationsFrom: 'Defera and Slice Pay',
  /** 2.5 second paragraph (account servicing consent) */
  servicingConsentParagraph:
    'If we or Slice Pay need to contact you for account servicing or to collect outstanding amounts owed to Defera (as creditor of record), you provide explicit consent to Defera, Slice Pay, as well as their respective servicers, agents, contractors, and collectors (including third-party collection agencies), to communicate with you via various methods.',
  /** 2.8 list — travel product */
  bulletTravelOwnership: 'Slice Pay owns the travel product (itinerary and booking arrangements) after booking.',
  /** 2.8 list — payments */
  bulletPayments:
    'All payments are made to Slice Pay. Payments you make to Slice Pay satisfy your installment obligations to Defera LLC.',
  /** 2.8 list — info */
  bulletInfoCollection:
    'Slice Pay will obtain and maintain passenger information necessary to facilitate installment tracking, payment processing, compliance verification, and the overall transaction process. This information may include, but is not limited to, personal identification details, payment information, and travel preferences.',
  /** 2.8 list — data sharing */
  bulletDataSharing:
    'By agreeing to these Terms, you consent to Defera sharing your booking information and personal data with Slice Pay as necessary to complete your transaction and provide ongoing service.',
  /** Privacy Policy §7.4 — intro (after heading) */
  privacy74Intro:
    'Defera partners with Slice Pay, a licensed financial services provider, to facilitate payment processing and installment transactions for flight bookings. In connection with this partnership:',
  privacy74DataSharing:
    'We share necessary passenger information with Slice Pay to enable payment processing, installment tracking, and compliance with financial service regulations. This information may include your name, contact details, payment information, booking details, and travel preferences.',
  privacy74PartnerResponsibilities:
    'Slice Pay owns the travel product after booking, and all payments are made to Slice Pay. Defera LLC is the creditor of record and owns the debt.',
  privacy74ThirdPartyPrivacy:
    'Your information shared with Slice Pay is subject to their privacy policy and data protection practices. We encourage you to review Slice Pay\'s privacy policy to understand how they collect, use, and protect your information.',
  privacy74OurRole:
    'Defera acts as an intermediary or broker, connecting you with Slice Pay\'s financial services. We do not control Slice Pay\'s data practices and are not responsible for their handling of your information once it has been shared with them.',
  privacy74ConsentClosing:
    'By using our services and agreeing to this Privacy Policy, you consent to the sharing of your information with Slice Pay as described above and acknowledge that such sharing is necessary to provide you with installment payment options for flight bookings.',
  /** HowItWorks embedded Terms §2.3 (differs slightly from main Terms) */
  howItWorks23PaymentReminders:
    'You agree to permit Defera and Slice Pay to send you payment reminders as needed. Furthermore, if you fail to make a payment owed to Defera (as creditor of record) or request cancellation of your booking, confirming the cancellation more than 24 hours after submitting your booking, your ticket(s) will be subject to cancellation in accordance with Slice Pay\'s policies. All payments made to Slice Pay and service fees paid to Defera are non-refundable, subject to the terms and conditions established by Slice Pay and applicable laws.',
  /** FlightDetailsModal — default-on-installment warning block */
  installmentDefaultCancelWarningBody:
    'You may choose to cancel your flight and default on your installment plan; however, doing so will negatively affect your standing with Defera and Slice Pay and will prevent you from booking future trips. Because Slice Pay operates across much of the layaway travel market—even when not publicly branded—defaulting here may also restrict your ability to use other installment-based travel providers. Defera will send any remaining balance to collections.',
  /** Analytics — cancelled row tooltip */
  analyticsCancelledTripsTooltip:
    'Total amount cancelled (base fare + service fee). Cancelled trips provide no revenue to Defera or Slice Pay.',
  /** Analytics — hold fees popover in partner view */
  analyticsHoldFeesRevenueTooltip: "Slice Pay's only revenue. 15% of pre-hold total.",
  /** EditCart admin — miles + installments strip (dynamic miles count in JSX) */
  editCartMilesInstallmentsPrefix: 'Customer must complete SlicePay for remaining cash. After payment confirmation, manually deplete ',
  editCartMilesInstallmentsSuffix: ' miles before pushing to Trips.',
  editCartMilesOnlyAdmin:
    "No SlicePay payment needed. Push to Trips when ready to deplete customer's miles.",
  /** Carts list — partner column / CTA */
  cartsPartnerShortLabel: 'Slice Pay',
  cartsOpenPartnerLink: 'Open Slice Pay',
  /** Admin zero trips modal — link type hint */
  adminCustomInstallmentLinkHint: 'Slice Pay link with custom params',
};

const CAPITAL = {
  isDeferaCapital: true,
  brand: 'Defera Capital',
  brandLLC: 'Defera Capital',
  brandPossessive: "Defera Capital's",
  partnerLong: 'Defera Capital and its financial partners',
  financialPartnerParen: 'Defera Capital and its financial partners',
  installmentsToPhrase: 'through Defera Capital and its financial partners',
  confirmationOfPaymentPhrase:
    'Upon confirmation of full payment completion by Defera Capital and its financial partners',
  facilitatesCollection:
    'Defera utilizes Defera Capital and its financial partners to facilitate payment collection on its behalf.',
  chargedBy: 'Defera Capital and its financial partners',
  payUnpaidTo: 'Defera Capital and its financial partners',
  coordinateWith: 'Defera Capital and its financial partners',
  governedByPolicies:
    "Defera Capital's policies, and those of its financial partners, and terms of service applicable at checkout",
  financialPartnerIdentity: 'Defera Capital and its financial partners',
  sharedWith: 'Defera Capital and its financial partners',
  deferaAndPartner: 'Defera and Defera Capital and its financial partners',
  weOrPartner: 'If we or Defera Capital or its financial partners need to contact you',
  consentParties: 'Defera, Defera Capital, its financial partners,',
  owedToCreditor: 'amounts owed to Defera (as creditor of record)',
  paidToPartner: 'Defera Capital and its financial partners',
  adjustedPer: "Defera Capital's policies and those of its financial partners",
  partnershipSectionTitle: 'Partnership with Defera Capital and Its Financial Partners',
  termsSection28Heading:
    '2.8. Creditor of Record and Partnership with Defera Capital and Its Financial Partners',
  travelProductOwner:
    'Defera Capital and its financial partners facilitate servicing of the travel product (itinerary and booking arrangements) after booking, and installment payments are processed through Defera Capital and its financial partners.',
  lossEligibility: 'Defera LLC, Defera Capital, and its financial partners, and services offered by other travel installment companies in the industry',
  verificationBy: 'Defera or Defera Capital and its financial partners',
  confirmedByBoth: 'Defera and Defera Capital and its financial partners',
  approvedByForRecurring: 'Defera Capital and its financial partners',
  subjectToVerification: 'verification and approval processes of Defera Capital and its financial partners',
  paymentDisputePartner: 'Defera Capital and its financial partners (our payment processing partners)',
  privacyPartnershipTitle: 'Partnership with Defera Capital and Its Financial Partners',
  privacyPartnersWith:
    'Defera partners with Defera Capital and its financial partners to facilitate payment processing and installment transactions',
  partnerResponsibilitiesTitle: "Defera Capital and its financial partners' responsibilities:",
  thirdPartyPrivacy: 'the applicable privacy policies of Defera Capital and its financial partners',
  analyticsPartnerName: 'Defera Capital',
  analyticsFeesLabel: 'Defera Capital fees',
  analyticsViewLabel: 'Defera Capital view',
  redirectDialogTitle: 'Redirecting to payment portal',
  redirectDialogBody:
    'You are being redirected to manage your installment plan. Use the email from your Defera checkout for access.',
  extensionAsideDefera: 'separate from your installment obligations with Defera Capital and its financial partners',
  defaultIndustryEntities:
    'Defera LLC, Defera Capital, its financial partners, and other travel installment companies in the industry',
  defaultStandingJeopardy: 'Defera and Defera Capital and its financial partners',
  defaultCancelInstallment: 'Defera and Defera Capital and its financial partners',
  externalPaymentLinkLabel: 'Installment payment link',
  webhookDescription: 'Installment payment webhook logs',
  adminBookingRefLabel: 'Booking reference (installments)',
  adminBookingRefHint: 'Payment reference used for installments (always visible to admins)',
  awaitingPartner: 'Awaiting payment',
  remindersSentBy: 'Defera Capital, its financial partners, or Defera',
  afterPartnerConfirmsEticket: 'after Defera Capital and its financial partners confirm',
  totalPaymentsCommitmentTarget: 'Defera Capital and its financial partners',
  noWarrantyFinancialServices:
    "Defera makes no representations or warranties regarding the financial services provided by Defera Capital and its financial partners and disclaims all liability for any issues arising from payment processing on Defera's behalf.",
  communicationsFrom: 'Defera, Defera Capital, and its financial partners',
  servicingConsentParagraph:
    'If we, Defera Capital, or its financial partners need to contact you for account servicing or to collect outstanding amounts owed to Defera (as creditor of record), you provide explicit consent to Defera, Defera Capital, its financial partners, as well as their respective servicers, agents, contractors, and collectors (including third-party collection agencies), to communicate with you via various methods.',
  bulletTravelOwnership:
    'Defera Capital and its financial partners facilitate servicing of the travel product (itinerary and booking arrangements) after booking.',
  bulletPayments:
    'Installment payments are processed through Defera Capital and its financial partners. Payments you make satisfy your installment obligations to Defera LLC.',
  bulletInfoCollection:
    'Defera Capital and its financial partners will obtain and maintain passenger information necessary to facilitate installment tracking, payment processing, compliance verification, and the overall transaction process. This information may include, but is not limited to, personal identification details, payment information, and travel preferences.',
  bulletDataSharing:
    'By agreeing to these Terms, you consent to Defera sharing your booking information and personal data with Defera Capital and its financial partners as necessary to complete your transaction and provide ongoing service.',
  privacy74Intro:
    'Defera partners with Defera Capital and its financial partners to facilitate payment processing and installment transactions for flight bookings. In connection with this partnership:',
  privacy74DataSharing:
    'We share necessary passenger information with Defera Capital and its financial partners to enable payment processing, installment tracking, and compliance with financial service regulations. This information may include your name, contact details, payment information, booking details, and travel preferences.',
  privacy74PartnerResponsibilities:
    'Defera Capital and its financial partners facilitate servicing of the travel product after booking, and installment payments are processed through them. Defera LLC is the creditor of record and owns the debt.',
  privacy74ThirdPartyPrivacy:
    'Your information shared with Defera Capital and its financial partners is subject to their applicable privacy policies and data protection practices. We encourage you to review those policies to understand how they collect, use, and protect your information.',
  privacy74OurRole:
    'Defera acts as an intermediary or broker, connecting you with installment payment services provided by Defera Capital and its financial partners. We do not control their data practices and are not responsible for their handling of your information once it has been shared with them.',
  privacy74ConsentClosing:
    'By using our services and agreeing to this Privacy Policy, you consent to the sharing of your information with Defera Capital and its financial partners as described above and acknowledge that such sharing is necessary to provide you with installment payment options for flight bookings.',
  howItWorks23PaymentReminders:
    'You agree to permit Defera and Defera Capital and its financial partners to send you payment reminders as needed. Furthermore, if you fail to make a payment owed to Defera (as creditor of record) or request cancellation of your booking, confirming the cancellation more than 24 hours after submitting your booking, your ticket(s) will be subject to cancellation in accordance with policies of Defera Capital and its financial partners. All installment payments processed for your booking and service fees paid to Defera are non-refundable, subject to applicable terms and laws.',
  installmentDefaultCancelWarningBody:
    'You may choose to cancel your flight and default on your installment plan; however, doing so will negatively affect your standing with Defera, Defera Capital, and its financial partners and will prevent you from booking future trips. Defaulting may also restrict your ability to use other installment-based travel providers. Defera will send any remaining balance to collections.',
  analyticsCancelledTripsTooltip:
    'Total amount cancelled (base fare + service fee). Cancelled trips provide no revenue to Defera, Defera Capital, or its financial partners.',
  analyticsHoldFeesRevenueTooltip:
    'Hold fees (15% of pre-hold total) as applicable to Defera Capital and its financial partners.',
  editCartMilesInstallmentsPrefix:
    'Customer must complete installment checkout for remaining cash. After payment confirmation, manually deplete ',
  editCartMilesInstallmentsSuffix: ' miles before pushing to Trips.',
  editCartMilesOnlyAdmin:
    'No installment checkout payment needed. Push to Trips when ready to deplete customer\'s miles.',
  cartsPartnerShortLabel: 'Defera Capital',
  cartsOpenPartnerLink: 'Open installment link',
  adminCustomInstallmentLinkHint: 'Installment link with custom params',
};

/**
 * @param {boolean} isDeferaCapital - from `useInstallmentsCheckoutProvider() === 'defera_capital'`
 */
export function getInstallmentsCopy(isDeferaCapital) {
  return isDeferaCapital ? { ...CAPITAL } : { ...SLICE };
}
